import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import throttle from 'lodash/throttle';

import { AppContext } from '@prisjakt/ui-app-context';

import { ViewportContext } from '../../../../components/viewport';

import ChevronButtons from '../buttons/chevron-buttons';
import Columns from './columns';
import ProductFinder from '../product-finder';

import { trackComparisonSelectionChanged } from '../../tracking';
import {
  CategoryPropsFragment,
  Product,
  ProductComparePageQuery,
  RelatedProduct,
} from '../../../../types';
import { features } from '../../constants';

import * as S from './styles';

type OwnProps = {
  category?: CategoryPropsFragment['category'];
  highlightDifferences: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  outerRef: React.RefObject<HTMLDivElement>;
  position: number;
  products: NonNullable<ProductComparePageQuery['products']>;
  relatedProducts: RelatedProduct[];
  setScrollPosition: (position: number) => void;
  showOverlay?: boolean;
  showSearchInHeader: boolean;
};

const CompareProducts = ({
  category,
  highlightDifferences,
  contentRef,
  outerRef,
  position,
  products,
  relatedProducts,
  setScrollPosition,
  showOverlay,
  showSearchInHeader,
}: OwnProps) => {
  const { linkTo } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);
  const history = useHistory();

  const [hasSideScroll, setHasSideScroll] = useState<boolean | null>(null);

  const update = useCallback(
    throttle(() => {
      setScrollPosition(
        contentRef && contentRef.current ? contentRef.current.scrollLeft : 0
      );
    }, 300),
    []
  );

  useEffect(() => {
    const currentWrapper = contentRef.current;
    if (!currentWrapper) return undefined;

    currentWrapper.addEventListener('scroll', update);

    return () => {
      if (currentWrapper) {
        currentWrapper.removeEventListener('scroll', update);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (contentRef.current && outerRef.current) {
      setHasSideScroll(
        contentRef.current.clientWidth < contentRef.current.scrollWidth
      );
    }
  }, [contentRef, outerRef, products.length]);

  const onSearchSelect = useCallback(
    (product: Product) => {
      history.push(
        linkTo('productCompare', {
          category,
          products: [...products, product],
        })
      );
      trackComparisonSelectionChanged(products.length + 1, 'add', 'search');
    },
    [category, history, linkTo, products]
  );

  return (
    <S.CompareProducts ref={outerRef}>
      {showOverlay && <S.Overlay />}
      {hasSideScroll && isDesktop && (
        <ChevronButtons
          contentRef={contentRef}
          position={position}
          setPosition={setScrollPosition}
        />
      )}
      <S.ProductsWrapper
        ref={contentRef}
        isScrollable={showSearchInHeader}
        hasScrollSnap={features.useCSSScrollSnap}
      >
        <Columns
          highlightDifferences={highlightDifferences}
          products={products}
        />
      </S.ProductsWrapper>
      {!showSearchInHeader && (
        <ProductFinder
          focusOnMount
          onSelect={onSearchSelect}
          relatedProducts={relatedProducts}
          showNewComparePageLayout
          showSearchInHeader={showSearchInHeader}
        />
      )}
    </S.CompareProducts>
  );
};

export default CompareProducts;
